'use client';

import * as React from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
  Control,
} from 'react-hook-form';

import { RadioGroup, RadioGroupProps } from '../radio';

export interface FormRadioGroupProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<RadioGroupProps, 'name' | 'defaultValue' | 'value' | 'onChange' | 'ref'> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];

  /**
   * Whether to show the error message
   * @default true
   */
  showError?: boolean;
}

/**
 * RadioGroup component integrated with React Hook Form
 * Handles string values from radio options
 */
export function FormRadioGroup<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, control, rules, showError = true, ...props }: FormRadioGroupProps<TFieldValues, TName>) {
  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  return (
    <RadioGroup
      {...props}
      ref={ref}
      value={value || ''}
      onChange={onChange}
      error={showError ? error?.message : undefined}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : undefined}
    />
  );
}

FormRadioGroup.displayName = 'FormRadioGroup';
